/**
 * Vista Trazabilidad — timeline real de un nº de serie, leído de
 * `stock` + `stock_event` vía `GET /api/trazabilidad?serie=...`.
 *
 * Input: nº de serie. Output: una card por cada fila stock que matchea
 * (puede haber > 1 si el gtin viene null y se aceptaron duplicados), cada
 * una con su timeline cronológico de eventos (INGRESO / RESERVA / EGRESO /
 * etc.) y los deltas de flags que dispararon.
 */

import { useState } from 'react';
import { Search, Loader2, Package, FileDown, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useTrazabilidadPorSerie,
  useDespachoPreviewTraza,
  useIngresoPreviewTraza,
  useTrazaJobByReferencia,
} from '@/api/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/notify';
import { generarPdfTrazaJob } from '@/lib/trazabilidad-job-pdf';
import { useSessionStore } from '@/stores/session';
import type {
  StockEvent,
  StockFlags,
  StockWithEvents,
} from '@/api/contracts/trazabilidad';

/** Qué preview está abierto en el modal. */
type PreviewTarget =
  | { tipo: 'despacho'; referencia: string }
  | { tipo: 'ingreso'; anmatTaskId: number; referencia: string | null };

const FLAG_LABEL: Record<keyof StockFlags, string> = {
  a: 'Disponible',
  na: 'Cuarentena',
  r: 'Reservado',
  p: 'Picking',
  o: 'Egresado',
};

/**
 * `na` agrupa dos situaciones: cuarentena y unidades que salieron con el
 * egreso anulado y todavía no volvieron. Se distinguen por el motivo del
 * evento (`pendiente_reingreso` al anular, `reingreso_fisico` al volver).
 */
const MOTIVOS_REINGRESO = new Set(['pendiente_reingreso', 'reingreso_fisico']);

function esMotivoReingreso(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object') return false;
  const motivo = (metadata as Record<string, unknown>).motivo;
  return typeof motivo === 'string' && MOTIVOS_REINGRESO.has(motivo);
}

function etiquetaFlag(k: keyof StockFlags, reingreso: boolean): string {
  return k === 'na' && reingreso ? 'Pendiente de reingreso' : FLAG_LABEL[k];
}

/** ISO → "dd/mm/aaaa HH:MM". */
function fmtFechaHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

export function TrazabilidadPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('serie') ?? '';
  const [input, setInput] = useState(initial);
  const [buscado, setBuscado] = useState(initial);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  const { data, isLoading, error, isFetching } = useTrazabilidadPorSerie(
    buscado || null,
  );

  const onBuscar = (e: React.FormEvent) => {
    e.preventDefault();
    const valor = input.trim();
    setBuscado(valor);
    if (valor) setSearchParams({ serie: valor });
    else setSearchParams({});
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-everex-azul">Trazabilidad</CardTitle>
          <p className="text-xs text-gray-600">
            Buscá un número de serie para ver su historial completo de
            movimientos sobre stock (ingresos, reservas, egresos, etc.).
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onBuscar} className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="N° de serie (ej. 693625)"
                className="pl-9 font-mono"
              />
            </div>
            <Button
              type="submit"
              disabled={isFetching || input.trim() === ''}
              className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
            >
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-1.5">Buscar</span>
            </Button>
          </form>
        </CardContent>
      </Card>

      {buscado && error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            No se pudo consultar la trazabilidad:{' '}
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      ) : buscado && isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          <span>Consultando timeline...</span>
        </div>
      ) : buscado && data && data.stocks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Package className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="font-semibold text-everex-azul">Serie no encontrada</h3>
            <p className="mt-1 text-xs text-gray-600">
              No hay registros de stock con el número de serie{' '}
              <span className="font-mono">{data.buscado}</span>.
            </p>
          </CardContent>
        </Card>
      ) : data && data.stocks.length > 0 ? (
        <div className="space-y-4">
          {data.stocks.length > 1 && (
            <Alert>
              <AlertDescription className="text-xs">
                Se encontraron <b>{data.stocks.length}</b> filas de stock con
                la misma serie (probablemente sin GTIN resuelto). Cada una
                muestra su propio timeline abajo.
              </AlertDescription>
            </Alert>
          )}
          {data.stocks.map((s) => (
            <StockCard key={s.id} stock={s} onPreview={setPreview} />
          ))}
        </div>
      ) : !buscado ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-gray-500">
            Ingresá un número de serie para ver su trazabilidad.
          </CardContent>
        </Card>
      ) : null}

      <MovimientoPreviewDialog target={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

function StockCard({
  stock,
  onPreview,
}: {
  stock: StockWithEvents;
  onPreview: (t: PreviewTarget) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle className="text-everex-azul">
            Stock #{stock.id} · <span className="font-mono">{stock.serie}</span>
          </CardTitle>
          <FlagsBadges
            flags={stock.flags}
            pendienteReingreso={
              stock.flags.na === 1 &&
              esMotivoReingreso(
                stock.events.reduce<(typeof stock.events)[number] | null>(
                  (ultimo, e) => (!ultimo || e.id > ultimo.id ? e : ultimo),
                  null,
                )?.metadata,
              )
            }
          />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
          <div className="col-span-2 md:col-span-4">
            <Dato label="Artículo">{stock.articuloNombre ?? '—'}</Dato>
          </div>
          <Dato label="GTIN" mono>
            {stock.gtin ?? '—'}
          </Dato>
          <Dato label="Lote" mono>
            {stock.lote ?? '—'}
          </Dato>
          <Dato label="Vencimiento">{stock.vencimiento ?? '—'}</Dato>
          <Dato label="GLN" mono>
            {stock.gln?.code ?? '—'}
            {stock.gln?.nombre && (
              <span className="ml-1 text-gray-500">({stock.gln.nombre})</span>
            )}
          </Dato>
          <Dato label="F. Ingreso">{fmtFechaHora(stock.fIngreso)}</Dato>
          <Dato label="F. Egreso">{fmtFechaHora(stock.fEgreso)}</Dato>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          Timeline ({stock.events.length} {stock.events.length === 1 ? 'evento' : 'eventos'})
        </div>
        {stock.events.length === 0 ? (
          <p className="mt-2 text-xs text-gray-500">
            Sin eventos registrados (caso raro — la fila stock existe pero el
            ledger está vacío).
          </p>
        ) : (
          <ol className="relative mt-3 space-y-5 border-l border-gray-200 pl-5">
            {stock.events.map((e) => (
              <EventoItem key={e.id} evento={e} onPreview={onPreview} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function EventoItem({
  evento,
  onPreview,
}: {
  evento: StockEvent;
  onPreview: (t: PreviewTarget) => void;
}) {
  const deltaEntries = (Object.keys(evento.deltas) as Array<keyof StockFlags>)
    .filter((k) => evento.deltas[k] !== 0)
    .map((k) => ({
      key: k,
      label: etiquetaFlag(k, esMotivoReingreso(evento.metadata)),
      delta: evento.deltas[k],
    }));

  return (
    <li className="relative">
      <span
        className="absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white"
      >
        •
      </span>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-semibold text-gray-800">{evento.evento}</span>
        <span className="text-[11px] text-gray-500">{fmtFechaHora(evento.createdAt)}</span>
      </div>
      {deltaEntries.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {deltaEntries.map((d) => (
            <span
              key={d.key}
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] font-mono',
                d.delta > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
              )}
              title={d.label}
            >
              {d.label} {d.delta > 0 ? `+${d.delta}` : d.delta}
            </span>
          ))}
        </div>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
        {evento.user && (
          <span>
            Usuario: <b>{evento.user.nombre ?? evento.user.username ?? `#${evento.user.id}`}</b>
          </span>
        )}
        {/* Despacho: solo en el EGRESO (el despacho ya trazado), con la
            referencia clickeable → preview. En el resto de los eventos no se
            muestra. Fallback al id si el backend viejo no la resolvió. */}
        {evento.evento !== 'EGRESO' ? null : evento.erpReferencia ? (
          <span>
            Despacho:{' '}
            <button
              type="button"
              onClick={() => onPreview({ tipo: 'despacho', referencia: evento.erpReferencia as string })}
              className="font-mono text-everex-azul underline-offset-2 transition hover:underline"
              title="Ver detalle del despacho"
            >
              {evento.erpReferencia}
            </button>
          </span>
        ) : evento.erpTaskDetailId != null ? (
          <span>
            ERP detail: <span className="font-mono">{evento.erpTaskDetailId}</span>
          </span>
        ) : null}
        {/* Ingreso: n° de remito clickeable → preview. */}
        {evento.anmatTaskId != null ? (
          <span>
            Ingreso:{' '}
            <button
              type="button"
              onClick={() =>
                onPreview({
                  tipo: 'ingreso',
                  anmatTaskId: evento.anmatTaskId as number,
                  referencia: evento.anmatReferencia ?? null,
                })
              }
              className="font-mono text-everex-azul underline-offset-2 transition hover:underline"
              title="Ver detalle del ingreso ANMAT"
            >
              {evento.anmatReferencia ?? `#${evento.anmatTaskId}`}
            </button>
          </span>
        ) : evento.anmatTaskDetailId != null ? (
          <span>
            ANMAT detail: <span className="font-mono">{evento.anmatTaskDetailId}</span>
          </span>
        ) : null}
      </div>
    </li>
  );
}

function FlagsBadges({
  flags,
  pendienteReingreso = false,
}: {
  flags: StockFlags;
  /** `na` por egreso anulado en tránsito (no cuarentena). */
  pendienteReingreso?: boolean;
}) {
  const activos = (Object.keys(flags) as Array<keyof StockFlags>).filter(
    (k) => flags[k] === 1,
  );
  if (activos.length === 0) {
    return (
      <span className="text-[11px] text-gray-500 italic">sin estado activo</span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {activos.map((k) => (
        <span
          key={k}
          className="rounded bg-everex-azul/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-everex-azul"
        >
          {etiquetaFlag(k, pendienteReingreso)}
        </span>
      ))}
    </div>
  );
}

/** Modal de vista previa de un movimiento (despacho o ingreso). */
function MovimientoPreviewDialog({
  target,
  onClose,
}: {
  target: PreviewTarget | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={target != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        {target?.tipo === 'despacho' ? (
          <DespachoPreview referencia={target.referencia} />
        ) : target?.tipo === 'ingreso' ? (
          <IngresoPreview anmatTaskId={target.anmatTaskId} referencia={target.referencia} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DespachoPreview({ referencia }: { referencia: string }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDespachoPreviewTraza(referencia);
  // Traza de egreso del despacho: la necesitan el PDF y el "Ir al despacho"
  // (para abrirlo en Completadas o en Pendientes según su estado).
  const { data: traza } = useTrazaJobByReferencia(referencia, 'egreso');
  const usuarioActual = useSessionStore((s) => s.usuario);

  // Mismas reglas que el "Descargar PDF" del detalle del despacho: sin traza
  // o con el egreso anulado no se imprime.
  const egresoAnulado = Boolean(data?.egresoAnulado) || traza?.status === 'anulada';
  const pdfDeshabilitado = !data || !traza || egresoAnulado;

  const handleDescargarPdf = () => {
    if (!data || !traza) return;
    void (async () => {
      try {
        await generarPdfTrazaJob({
          traza,
          comprobante: data,
          impresoPor: usuarioActual?.nombre ?? null,
        });
      } catch (err) {
        toast.error(
          `No se pudo generar el PDF: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    })();
  };

  // Abre el detalle en /despacho. El detalle no tiene URL propia (vive en el
  // estado de DespachoPage), así que se le pasa lo mínimo por el state de la
  // navegación y DespachoPage arma la tarea. Traza hecha = misma regla que
  // Completadas: success, o anulada con el remito anulado.
  const handleIrAlDespacho = () => {
    if (!data) return;
    const trazaHecha =
      traza?.status === 'success' || (traza?.status === 'anulada' && Boolean(data.remitoAnulado));
    void navigate('/despacho', {
      state: {
        abrirDespacho: {
          referencia: data.referencia,
          tipo: data.tipo,
          numeroRemito: data.numeroAfip,
          trazaHecha,
        },
      },
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Despacho · <span className="font-mono">{referencia}</span>
        </DialogTitle>
        <DialogDescription className="text-xs">Vista previa.</DialogDescription>
      </DialogHeader>
      {isLoading ? (
        <PreviewCargando />
      ) : error ? (
        <PreviewError error={error} />
      ) : data ? (
        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <Dato label="N° AFIP" mono>{data.numeroAfip ?? '—'}</Dato>
            <Dato label="GLN destino" mono>{data.glnDestino ?? '—'}</Dato>
            <Dato label="Cliente" mono>{data.comprobante.clienteId ?? '—'}</Dato>
          </div>
          <SeriesTable
            rows={(data.partidas ?? []).map((p) => ({
              sku: p.sku,
              serie: p.numeroDeSerie,
              lote: p.lote,
              vto: p.fechaVencimiento,
            }))}
          />
        </div>
      ) : null}
      <DialogFooter className="gap-2 sm:gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleDescargarPdf}
          disabled={pdfDeshabilitado}
          className="text-xs"
          title={
            egresoAnulado
              ? 'No se puede imprimir: el egreso de este despacho está anulado.'
              : 'Descargar comprobante de trazabilidad en PDF'
          }
        >
          <FileDown className="mr-1.5 h-3.5 w-3.5" />
          Descargar PDF
        </Button>
        <Button size="sm" onClick={handleIrAlDespacho} disabled={!data} className="text-xs">
          Ir al despacho
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </DialogFooter>
    </>
  );
}

function IngresoPreview({
  anmatTaskId,
  referencia,
}: {
  anmatTaskId: number;
  referencia: string | null;
}) {
  const { data, isLoading, error } = useIngresoPreviewTraza(anmatTaskId);
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Ingreso ANMAT · <span className="font-mono">{referencia ?? `#${anmatTaskId}`}</span>
        </DialogTitle>
        <DialogDescription className="text-xs">Vista previa.</DialogDescription>
      </DialogHeader>
      {isLoading ? (
        <PreviewCargando />
      ) : error ? (
        <PreviewError error={error} />
      ) : data ? (
        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <Dato label="Remito">{data.nRemito ?? '—'}</Dato>
            <Dato label="GLN origen" mono>{data.glnOrigen ?? '—'}</Dato>
            <Dato label="GLN destino" mono>{data.glnDestino ?? '—'}</Dato>
            <Dato label="Origen">{data.razonSocialOrigen ?? '—'}</Dato>
            <Dato label="Estado">{data.estado ?? '—'}</Dato>
          </div>
          <SeriesTable
            rows={data.series.map((s) => ({
              sku: s.gtin,
              serie: s.numeroSerial,
              lote: s.lote,
              vto: s.vencimiento,
            }))}
          />
        </div>
      ) : null}
    </>
  );
}

function SeriesTable({
  rows,
}: {
  rows: Array<{ sku: string | null; serie: string | null; lote: string | null; vto: string | null }>;
}) {
  if (rows.length === 0) {
    return <p className="text-xs text-gray-500">Sin series.</p>;
  }
  return (
    <div className="max-h-64 overflow-auto rounded-md border border-gray-200">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase text-gray-600">
          <tr>
            <th className="px-2 py-1.5 text-left">SKU / GTIN</th>
            <th className="px-2 py-1.5 text-left">Serie</th>
            <th className="px-2 py-1.5 text-left">Lote</th>
            <th className="px-2 py-1.5 text-left">Vto</th>
          </tr>
        </thead>
        <tbody className="text-gray-800">
          {rows.map((r, i) => (
            <tr key={`${r.serie ?? '?'}-${i}`} className="border-t border-gray-100">
              <td className="px-2 py-1.5 font-mono">{r.sku ?? '—'}</td>
              <td className="px-2 py-1.5 font-mono">{r.serie ?? '—'}</td>
              <td className="px-2 py-1.5 font-mono">{r.lote ?? '—'}</td>
              <td className="px-2 py-1.5">{r.vto ? String(r.vto).slice(0, 10) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreviewCargando() {
  return (
    <div className="flex items-center justify-center py-8 text-gray-600">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
      <span className="text-xs">Cargando detalle…</span>
    </div>
  );
}

function PreviewError({ error }: { error: unknown }) {
  return (
    <Alert variant="destructive">
      <AlertDescription className="text-xs">
        No se pudo cargar el detalle:{' '}
        {error instanceof Error ? error.message : String(error)}
      </AlertDescription>
    </Alert>
  );
}

function Dato({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className={cn('mt-0.5 text-gray-800', mono && 'font-mono')}>{children}</div>
    </div>
  );
}
